const MAX_SAFE_CENTS = Number.MAX_SAFE_INTEGER;

const brlInputFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toSafeCents = (integerDigits, decimalDigits = '') => {
  const centsText = `${integerDigits}${decimalDigits.padEnd(2, '0')}`
    .replace(/^0+(?=\d)/, '');
  const cents = Number(centsText || '0');
  return Number.isSafeInteger(cents) && cents <= MAX_SAFE_CENTS ? cents : null;
};

const groupIntegerDigits = (digits) => {
  const normalized = String(digits || '0').replace(/^0+(?=\d)/, '') || '0';
  return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Converte uma entrada monetária brasileira estrita em centavos.
 * Retorna null para entradas inválidas ou fora do intervalo seguro do JavaScript.
 */
export const parseBrazilianMoneyToCents = (input) => {
  if (typeof input !== 'string') return null;

  const normalized = input.replace(/\u00a0/g, ' ').trim();
  const match = normalized.match(
    /^(?:R\$\s*)?(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{1,2}))?$/
  );

  if (!match) return null;

  const integerDigits = match[1].replace(/\./g, '');
  return toSafeCents(integerDigits, match[2] || '');
};

export const decimalToCents = (value) => {
  if (!Number.isFinite(value) || value < 0) return null;
  const cents = Math.round((value + Number.EPSILON) * 100);
  return Number.isSafeInteger(cents) ? cents : null;
};

/** Formata centavos para o valor editável do campo, sem repetir o prefixo R$. */
export const formatCentsForInput = (cents) => (
  Number.isSafeInteger(cents) && cents >= 0
    ? brlInputFormatter.format(cents / 100)
    : ''
);

/**
 * Formata a digitação natural: dígitos representam reais e os centavos só
 * começam apenas depois da vírgula. Pontos são sempre agrupadores, inclusive
 * durante edições parciais como 1.0000. Nunca inferir decimal pelo agrupamento.
 */
export const formatMoneyInputDraft = (input) => {
  if (input === null || input === undefined) return '';

  const rawValue = String(input)
    .replace(/\u00a0/g, ' ')
    .replace(/^\s*R\$\s*/i, '')
    .replace(/\s/g, '');

  if (!rawValue || rawValue.includes('-')) return '';

  const separatorIndex = rawValue.indexOf(',');

  if (separatorIndex >= 0) {
    const integerDigits = rawValue.slice(0, separatorIndex).replace(/\D/g, '');
    const decimalDigits = rawValue.slice(separatorIndex + 1).replace(/\D/g, '').slice(0, 2);
    if (!integerDigits && !decimalDigits) return '0,';
    return `${groupIntegerDigits(integerDigits)}${','}${decimalDigits}`;
  }

  const integerDigits = rawValue.replace(/\D/g, '');
  return integerDigits ? groupIntegerDigits(integerDigits) : '';
};

export const parseMoneyInputDraftToCents = (input) => {
  const draft = formatMoneyInputDraft(input);
  if (!draft) return null;
  return parseBrazilianMoneyToCents(draft.endsWith(',') ? draft.slice(0, -1) : draft);
};

export const finalizeMoneyInput = (input) => {
  const cents = parseMoneyInputDraftToCents(input);
  return cents === null ? '' : formatCentsForInput(cents);
};

// Alias preservado para consumidores existentes da máscara.
export const maskMoneyInput = formatMoneyInputDraft;

const legacyStringToCents = (value) => {
  const brazilianCents = parseBrazilianMoneyToCents(value);
  if (brazilianCents !== null) return brazilianCents;

  const normalized = value.trim();
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  return decimalToCents(Number(normalized));
};

export const getAmountCents = (record) => {
  if (!record || typeof record !== 'object') return null;

  if (
    Number.isSafeInteger(record.amountCents) &&
    record.amountCents >= 0
  ) {
    return record.amountCents;
  }

  if (typeof record.valor === 'number') {
    return decimalToCents(record.valor);
  }

  if (typeof record.valor === 'string') {
    return legacyStringToCents(record.valor);
  }

  return null;
};

export const centsToLegacyValue = (cents) =>
  Number.isSafeInteger(cents) && cents >= 0 ? cents / 100 : null;

export const parseBrazilianMoney = (input) => {
  const cents = parseBrazilianMoneyToCents(input);
  return cents === null ? null : centsToLegacyValue(cents);
};

const brlFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatCents = (cents) => {
  if (!Number.isSafeInteger(cents)) return 'Valor inválido';
  return brlFormatter.format(cents / 100);
};